import React, { useState, useEffect, useRef } from "react";
import "./CommonSelect.scss";
import CommonText from "../CommonText/CommonText";
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from "react-icons/md";

interface Option {
  id: string | number;
  [key: string]: any;
}

interface CommonSelectProps {
  options: Option[]; // List of dropdown options
  selected: string | number; // Currently selected value (id)
  onSelect: (value: string | number) => void; // Function to handle option selection
  label?: string; // Optional label for the dropdown
  className?: string; // Optional className for styling
  disabled?: boolean; // Disable the dropdown
  placeholder?: string; // Placeholder when no option is selected
  error?: string; // Optional error message to display
  nameKey?: string;
  valueKey?: string;
}

const CommonSelect: React.FC<CommonSelectProps> = ({
  options,
  selected,
  onSelect,
  label,
  className = "",
  disabled = false,
  placeholder = "Select an option",
  error = "",
  nameKey = "name",
  valueKey = "id",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState(""); 
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (options.length === 1 && !selected) {
      onSelect(options[0].id);
    }
  }, [options, selected, onSelect]);

  const handleClickOutside = (event: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setIsOpen(false);
      setSearchTerm("");
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const toggleCommonSelect = () => {
    if (!disabled) setIsOpen((prev) => !prev);
  };

  const handleSelect = (option: Option) => {
    const returnValue = valueKey ? option[valueKey] : option.id;
    onSelect(returnValue);
    setIsOpen(false);
    setSearchTerm(""); 
  };

  const selectedOption = options.find((option) => option[valueKey] === selected);
  const selectedName = selectedOption ? selectedOption[nameKey] : placeholder;

  const filteredOptions = options.filter((option) => 
    (option[nameKey] as string).toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div
      ref={dropdownRef}
      className={`dropdown ${className} ${disabled ? "disabled" : ""} ${isOpen ? "open" : ""}`}>
      {label && <label>{label}</label>}

      <div className="dropdown-container" onClick={toggleCommonSelect}>
        <div
          className={`dropdown-selected ${
            selectedOption ? "" : "placeholder-added"
          }`}>
          {selectedName}
        </div>
        {!disabled && (
          <div className="dropdown-arrow">
            {isOpen ? <MdKeyboardArrowUp /> : <MdKeyboardArrowDown />}
          </div>
        )}
      </div>

      {isOpen && !disabled && (
        <div className="dropdown-menu">    
          <CommonText
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search..."
            className="pe-2 ps-2"
            autoFocus={true}
          />   
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option) => (
              <div
                key={option.id}
                className="dropdown-option"
                onClick={() => handleSelect(option)}>
                {option[nameKey]}
              </div>
            ))
          ) : (
            <div className="dropdown-option disabled">No options found</div>
          )}
        </div>
      )}

      {error && (
        <div className="text-start position-absolute common-error-message">
          {error}
        </div>
      )}
    </div>
  );
};

export default CommonSelect;
